import { useEffect, useState } from "react";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import PropertyCard from "@/components/PropertyCard";
import SearchFilters from "@/components/SearchFilters";
import { Property } from "@/types/property";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useSelector } from "react-redux";
import API_URL from "@/config";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";
import useDocumentTitle from "@/components/Helmet";
import MapView from "@/components/GoogleMap/MapView";

interface PropertyFilters {
  category?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  city?: string;
  county?: string;
  search?: string;
  sortBy?: 'price-asc' | 'price-desc' | 'date-desc' | 'date-asc';
}

export default function Home() {
  useDocumentTitle("Home");

  const { toast } = useToast();
  const [filters, setFilters] = useState<PropertyFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [location] = useLocation();
  const { user } = useSelector((state: any) => state.auth);
  // const queryClient = useQueryClient();
  const [limit, setLimit] = useState(6);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [isMapView, setIsMapView] = useState(false);

  const showFavorites = location === "/favorites";



  const fetchProperties = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== undefined && value !== "")
        ),
        limit: limit.toString(),
        page: currentPage.toString(),
      });
      const res = await axios.get(API_URL + `/api/properties?${new URLSearchParams(params)}`)
        .then((res) => {
          setProperties(res?.data?.properties);
        })

    } catch (error: any) {
      toast({
        title: "Error!",
        description: error?.response?.data?.message,
      })
    }
    finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchProperties();
  }, [filters, currentPage, limit])

  const handleFilterChange = (newFilters: PropertyFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {!showFavorites && <HeroSection onSearch={handleFilterChange} />}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="text-page-title">
              {showFavorites ? "Your Saved Properties" : "Properties for Sale"}
            </h2>
            <p className="text-gray-600" data-testid="text-results-count">
              {properties.length} {showFavorites ? "saved properties" : "results found"}
            </p>
          </div>

          {!showFavorites && (
            <div className="flex items-center w-full md:w-auto space-x-4 mt-4 md:mt-0">
              <SearchFilters onFilterChange={handleFilterChange} filters={filters} setIsMapView={setIsMapView} isMapView={isMapView} />
            </div>
          )}
        </div>

        {isMapView && (
          <MapView properties={properties} />
        )}

        {/* Property Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : properties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {properties.map((property) => (
              <PropertyCard
                key={property._id}
                property={property}
                showFavoriteButton={!!user}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <i className="fas fa-home text-6xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {showFavorites ? "No saved properties yet" : "No properties found"}
            </h3>
            <p className="text-gray-600 mb-6">
              {showFavorites
                ? "Start browsing properties and save your favorites to see them here."
                : "Try adjusting your search criteria to find more properties."
              }
            </p>
            {showFavorites && (
              <Button onClick={() => window.location.href = "/"} data-testid="button-browse-properties">
                Browse Properties
              </Button>
            )}
          </div>
        )}

        {/* Pagination */}
        {!showFavorites && (
          <div className="flex justify-center items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              data-testid="button-previous-page"
            >
              <i className="fas fa-chevron-left mr-2"></i>Previous
            </Button>
            <span className="px-4 py-2 text-gray-600" data-testid="text-current-page">
              Page {currentPage}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={properties.length < limit}
              data-testid="button-next-page"
            >
              Next<i className="fas fa-chevron-right ml-2"></i>
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
